import { defu } from 'defu'
import { appendResponseHeader, H3Event } from 'h3'
import type { FetchError, FetchOptions } from 'ohmyfetch' 

export interface CustomFetchOptions extends Omit<FetchOptions<'json'>, 'onResponse' | 'onRequest' | 'onResponseError'> {
  domain?: 'UPLOAD' | string;
  onResponse?(ctx: { response: Response; data: any }): void;
  onResponseError?(ctx: { response?: Response; error?: any }): void;
}

export async function useCustomFetch<T> (
  url: string,
  options: CustomFetchOptions = {}
): Promise<T> {

  const config = useRuntimeConfig()


  const getCookieString = (): string => {
    const authGuest = useCook('auth:guest')
    const unitVolume = useCook('unit_volume')
    const unitTemperature = useCook('unit_temperature')
    const unitWeight = useCook('unit_weight')
    const unitTds = useCook('unit_tds')
    const unitLength = useCook('unit_length')
    const details = useCook('details')
    const cookieUseExperimentsRegion = useCook('useExperimentsRegion')

    let cookieString = ''
    if (unitVolume.value) cookieString += `unit_volume=${unitVolume.value}; `
    if (unitTemperature.value) cookieString += `unit_temperature=${unitTemperature.value}; `
    if (unitWeight.value) cookieString += `unit_weight=${unitWeight.value}; `
    if (unitTds.value) cookieString += `unit_tds=${unitTds.value}; `
    if (unitLength.value) cookieString += `unit_length=${unitLength.value}; `
    if (authGuest.value) cookieString += `auth:guest=${authGuest.value}; `
    if (details.value) cookieString += `details=${details.value}; `
    if (cookieUseExperimentsRegion.value) cookieString += `useExperimentsRegion=${cookieUseExperimentsRegion.value}; `

    return cookieString
  }


  const getHeader = (name: string) => {
    const headers = useRequestHeaders([name])
    return headers[name]
  }


  const userAuth = useCook('auth:token')
  const guestAuth = useCook('auth:guest')
  const apiCookie = useCook('auth:api')
  const apiKey = apiCookie.value ? apiCookie : useApiSecret()

  const debugFor = useDebugListFor()

  const configOrigin = ref('')
  const configApiSecret = ref('')
  const configApiSecretDev = ref('')
  const event = useRequestEvent() // доступен только на сервере


  if (import.meta.server) {
    configOrigin.value = config.origin ?? ''
    configApiSecret.value = config.apiSecret ?? null
  }else{
    configApiSecretDev.value = config.public.apiSecretDev ?? null
  }

  url = url.replace(/^\/api/, '')


  if (import.meta.server) {
    url = config.public.baseAPILocal + url
  } else {
    if (options.domain === 'UPLOAD') {
      url = config.public.baseAPIUpload + url
    } else {
      url = config.public.baseAPI + url
    }
  }


  const { $uniq, hooks } = useNuxtApp()
  let uniq = ''
  let rniq = ''
  if (import.meta.client) {
    const tmpUniq = $uniq.code()
    uniq = tmpUniq.code
    rniq = tmpUniq.rnd
  }


  const headers: Record<string, string> = {
    'Authorization': userAuth.value ? `Bearer ${userAuth.value}` : '',
    'X-Access': apiKey.value ?? '',
    'X-Guest': guestAuth.value ?? '',
    'X-Origin': configOrigin.value ?? '',
    'X-Pass': configApiSecretDev.value ? configApiSecretDev.value : (configApiSecret.value ?? ''),
    'X-Pass-Old':  (configApiSecret.value ?? ''),
    'X-Uniq': uniq ?? '',
    'X-Rniq': rniq ?? '',
    'Cookie': getCookieString()
  }


  if (import.meta.server) {
    headers['CF-IPCountry'] = getHeader('cf-ipcountry') || ''
  }


  const defaults: FetchOptions<'json'> = {
    method: options.method || 'GET',

    credentials: 'include',
    headers
  }


  const fetchOptions = defu(options, defaults) as FetchOptions<'json'>




  delete (fetchOptions as any).onResponse
  delete (fetchOptions as any).onResponseError

  let data: T

  try {

    const response: T = await $fetch<T>(url, fetchOptions)


    if (options.onResponse) {

      options.onResponse({
        response: ({} as Response),
        data: response
      })
    }

    if (import.meta.server) {

    }


    if (import.meta.client && !options.method) {
      hooks.callHook('custom:fetch:get', response).catch(() => {})
    }
    hooks.callHook('custom:fetch:any', response).catch(() => {})


    if ((response as any)?.details) {
      debugFor.value.push(response) 
    }

    data = response
  } catch (error: any) {

    if (options.onResponseError) {
      options.onResponseError({
        response: undefined, 
        error
      })
    }

    throw error
  }

  return data
}